/**
 * Ticket Lifecycle v2 — FOH Timer Handoff (Batch 3)
 *
 * Manager / owner / org-admin "Advisor Performance" surface for the
 * Analytics page. Where the Advisor Passport tells the story of a single
 * person, this tells the story of the SHOP's front-of-house flow:
 *
 *   A. Shop-level advisor overview (live queue + 30-day handoff totals)
 *   B. Per-advisor performance table
 *   C. Handoff reason breakdown
 *   D. FOH bottleneck / aging view
 *   + Pause-kick (tech-blocking) vs Stop-kick (closeout) analytics
 *
 * All numbers come from `useShopAdvisorAnalytics`, which reads real
 * custody / advisor-action events (no mock data). Metrics that cannot be
 * computed from the current sample render as "—" rather than a fake 0.
 *
 * Window: fixed last-30-days, matching the Passport advisor metrics
 * window (`ADVISOR_METRICS_WINDOW_DAYS`). A full custom date-range
 * system is intentionally out of scope for Batch 3 — the underlying
 * timing hooks are hard-coded to 30 days, so the label says so plainly.
 */
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Users,
  Clock,
  AlertTriangle,
  PauseCircle,
  StopCircle,
  HandMetal,
} from 'lucide-react';
import { useShopAdvisorAnalytics } from '@/hooks/tickets/useShopAdvisorAnalytics';
import {
  SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS,
  CUSTODY_AGING_THRESHOLDS_MS,
} from '@/lib/tickets/lifecycle/shopAdvisorAnalytics';

const AGING_WARNING_MIN = Math.round(CUSTODY_AGING_THRESHOLDS_MS.warning / 60_000);
const AGING_URGENT_MIN = Math.round(CUSTODY_AGING_THRESHOLDS_MS.urgent / 60_000);

interface Props {
  shopId: string;
}

function formatMinutes(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value < 1) return '< 1m';
  if (value < 60) return `${Math.round(value)}m`;
  const hours = Math.floor(value / 60);
  const minutes = Math.round(value % 60);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

function formatRate(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${Math.round(value * 100)}%`;
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'warning' | 'danger';
}) {
  return (
    <div className="p-3 bg-muted/50 rounded-lg">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div
        className={
          'text-xl font-semibold tabular-nums ' +
          (tone === 'danger'
            ? 'text-destructive'
            : tone === 'warning'
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-foreground')
        }
      >
        {value}
      </div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function AdvisorPerformanceDashboard({ shopId }: Props) {
  const { data, isLoading, isError, error } = useShopAdvisorAnalytics({ shopId });

  const windowLabel = `Last ${SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS} days`;

  if (isLoading) {
    return (
      <Card data-testid="advisor-performance-dashboard">
        <CardHeader>
          <CardTitle>Advisor Performance</CardTitle>
          <CardDescription>{windowLabel}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading advisor analytics…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="advisor-performance-dashboard">
        <CardHeader>
          <CardTitle>Advisor Performance</CardTitle>
          <CardDescription className="text-destructive">
            Failed to load advisor analytics. The custody / advisor-action
            migrations may not be applied to this Supabase project.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            {(error as Error)?.message || 'Unknown error.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { overview, triggerBreakdown, reasonBreakdown, perAdvisor, aging } =
    data;

  const noActivity =
    overview.currentFohQueueCount === 0 &&
    overview.ticketsAcceptedCount === 0 &&
    overview.ticketsKickedToFohCount === 0 &&
    overview.ticketsKickedBackToTechCount === 0 &&
    perAdvisor.length === 0;

  return (
    <div className="space-y-6" data-testid="advisor-performance-dashboard">
      {/* ── A. Shop-level overview ───────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Shop Advisor Overview</CardTitle>
          </div>
          <CardDescription>
            FOH / Advisor analytics · {windowLabel}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {noActivity ? (
            <p className="text-sm text-muted-foreground">
              No advisor activity yet — nothing has been kicked to FOH in the
              last {SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS} days, and the FOH queue
              is empty.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Live queue snapshot */}
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  Right now
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat
                    label="FOH queue"
                    value={String(overview.currentFohQueueCount)}
                  />
                  <Stat
                    label="Pending advisor accept"
                    value={String(overview.pendingAdvisorAcceptCount)}
                    tone={
                      overview.pendingAdvisorAcceptCount > 0
                        ? 'warning'
                        : 'default'
                    }
                  />
                  <Stat
                    label="With advisor"
                    value={String(overview.withAdvisorCount)}
                  />
                  <Stat
                    label="Pending tech accept"
                    value={String(overview.pendingTechAcceptCount)}
                  />
                </div>
              </div>

              {/* Window totals */}
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  {windowLabel}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat
                    label="Tickets accepted"
                    value={String(overview.ticketsAcceptedCount)}
                  />
                  <Stat
                    label="Kicked to FOH"
                    value={String(overview.ticketsKickedToFohCount)}
                  />
                  <Stat
                    label="Kicked back to tech"
                    value={String(overview.ticketsKickedBackToTechCount)}
                  />
                  <Stat
                    label="Avg accept lag"
                    value={formatMinutes(overview.averageAcceptLagMinutes)}
                  />
                  <Stat
                    label="Avg FOH custody"
                    value={formatMinutes(overview.averageFohCustodyMinutes)}
                  />
                  <Stat
                    label="Pause-kicks"
                    value={String(overview.pauseKickCount)}
                    hint="Tech-blocking"
                  />
                  <Stat
                    label="Stop-kicks"
                    value={String(overview.stopKickCount)}
                    hint="Closeout"
                  />
                  <Stat
                    label="Manual kicks"
                    value={String(overview.manualKickCount)}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── D. FOH bottleneck / aging ────────────────────────────────── */}
      <Card data-testid="advisor-foh-aging">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <CardTitle>FOH Queue Bottleneck</CardTitle>
          </div>
          <CardDescription>
            Tickets waiting for an advisor to accept · aging at{' '}
            {AGING_WARNING_MIN}m, urgent at {AGING_URGENT_MIN}m
          </CardDescription>
        </CardHeader>
        <CardContent>
          {aging.pendingAdvisorAcceptCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tickets are currently waiting for an advisor to accept.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                label="Pending accept"
                value={String(aging.pendingAdvisorAcceptCount)}
              />
              <Stat
                label="Aging"
                value={String(aging.agingCount)}
                tone={aging.agingCount > 0 ? 'warning' : 'default'}
                hint="Waiting 5m+"
              />
              <Stat
                label="Urgent"
                value={String(aging.urgentCount)}
                tone={aging.urgentCount > 0 ? 'danger' : 'default'}
                hint="Waiting 10m+"
              />
              <Stat
                label="Oldest wait"
                value={formatMinutes(aging.oldestTicketAgeMinutes)}
                hint={`Avg ${formatMinutes(aging.averageCurrentDwellMinutes)}`}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Pause-kick vs Stop-kick ──────────────────────────────────── */}
      <Card data-testid="advisor-pause-stop-analytics">
        <CardHeader>
          <CardTitle>Handoff Type — Tech-blocking vs Closeout</CardTitle>
          <CardDescription>
            Pause-kicks unblock a tech mid-job; stop-kicks hand off a
            completed scope for the next step.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tech-blocking */}
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <PauseCircle className="h-4 w-4 text-amber-600" />
                <span className="font-medium">Tech-blocking handoffs</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Stat
                  label="Count"
                  value={String(triggerBreakdown.pauseKickToFohCount)}
                />
                <Stat
                  label="Avg accept"
                  value={formatMinutes(
                    triggerBreakdown.averageAcceptLagMinutesPauseKick,
                  )}
                />
                <Stat
                  label="Avg FOH dwell"
                  value={formatMinutes(
                    triggerBreakdown.averageFohDwellMinutesPauseKick,
                  )}
                />
                <Stat
                  label="Kickback rate"
                  value={formatRate(triggerBreakdown.kickbackRatePauseKick)}
                />
              </div>
            </div>

            {/* Closeout */}
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <StopCircle className="h-4 w-4 text-primary" />
                <span className="font-medium">Closeout handoffs</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Stat
                  label="Count"
                  value={String(triggerBreakdown.stopKickToFohCount)}
                />
                <Stat
                  label="Avg accept"
                  value={formatMinutes(
                    triggerBreakdown.averageAcceptLagMinutesStopKick,
                  )}
                />
                <Stat
                  label="Avg FOH dwell"
                  value={formatMinutes(
                    triggerBreakdown.averageFohDwellMinutesStopKick,
                  )}
                />
                <Stat
                  label="Kickback rate"
                  value={formatRate(triggerBreakdown.kickbackRateStopKick)}
                />
              </div>
            </div>

            {/* Manual / implicit */}
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <HandMetal className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Manual &amp; implicit</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Stat
                  label="Manual kicks"
                  value={String(triggerBreakdown.manualKickToFohCount)}
                />
                <Stat
                  label="Implicit takeovers"
                  value={String(triggerBreakdown.implicitTakeoverCount)}
                />
                {triggerBreakdown.unknownTriggerKickCount > 0 && (
                  <Stat
                    label="Unclassified"
                    value={String(triggerBreakdown.unknownTriggerKickCount)}
                    hint="Legacy rows"
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── C. Reason breakdown ──────────────────────────────────────── */}
      <Card data-testid="advisor-reason-breakdown">
        <CardHeader>
          <CardTitle>Handoff Reasons</CardTitle>
          <CardDescription>
            Why tickets were kicked to FOH · {windowLabel}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reasonBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No handoff reasons recorded in the last{' '}
              {SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS} days.
            </p>
          ) : (
            <div className="space-y-1.5">
              {reasonBreakdown.slice(0, 12).map((r) => (
                <div
                  key={`${r.trigger}|${r.value}`}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span>{r.label}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {r.trigger === 'pause_kick'
                        ? 'tech-blocking'
                        : r.trigger === 'stop_kick'
                          ? 'closeout'
                          : r.trigger}
                    </Badge>
                  </div>
                  <span className="font-mono font-medium tabular-nums">
                    {r.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── B. Per-advisor table ─────────────────────────────────────── */}
      <Card data-testid="advisor-performance-table">
        <CardHeader>
          <CardTitle>Advisor Performance Detail</CardTitle>
          <CardDescription>
            Per-advisor handoff metrics · {windowLabel}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {perAdvisor.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No advisor activity in the last{' '}
              {SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS} days.
            </p>
          ) : (
            <div className="overflow-x-auto">
              {/* min-w lets the 11-column table scroll horizontally on
                  iPad/tablet instead of crushing columns. */}
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Advisor</TableHead>
                    <TableHead className="text-right">Accepted</TableHead>
                    <TableHead className="text-right">Avg accept</TableHead>
                    <TableHead className="text-right">p50</TableHead>
                    <TableHead className="text-right">p95</TableHead>
                    <TableHead className="text-right">Avg FOH</TableHead>
                    <TableHead className="text-right">Kicked back</TableHead>
                    <TableHead className="text-right">Kicked to FOH</TableHead>
                    <TableHead className="text-right">With now</TableHead>
                    <TableHead className="text-right">Tech-block</TableHead>
                    <TableHead className="text-right">Closeout</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {perAdvisor.map((a) => (
                    <TableRow key={a.advisorUserId}>
                      <TableCell className="font-medium">
                        {a.displayName}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.ticketsAcceptedCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMinutes(a.averageAcceptLagMinutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMinutes(a.p50AcceptLagMinutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMinutes(a.p95AcceptLagMinutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMinutes(a.averageFohCustodyMinutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.ticketsKickedBackToTechCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.ticketKickedToFohCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.currentWithAdvisorCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.pauseKickHandledCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.stopKickHandledCount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        Shop-scoped analytics from live custody &amp; advisor-action events.
        Fixed {SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS}-day window.
      </p>
    </div>
  );
}
